import { useMemo, useState } from 'react'

import clsx from 'clsx'

import { cveColor, cveLabel } from '@/common/app/modules/media/articleCreator'
import { KeywordItem } from '@/common/model/media/articleCreator'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import TextButton from '@/components/view/atoms/button/TextButton'
import ListHeaderSortButton from '@/components/view/atoms/icon/ListHeaderSortButton'
import Selector, { SelectorSize } from '@/components/view/atoms/selector/Selector'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Line from '@/components/view/layouts/area/Line'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

type SortKey = 'keyword' | 'monthly_search_volume' | 'cve' | 'search_rank'
type SortOrder = 'asc' | 'desc'
type FilterMode = 'input' | 'all'

interface Props {
  keywords: KeywordItem[]
  inputKeyword: string
  selectedKeywords: KeywordItem[]
  onSelect(keywords: KeywordItem[]): void
}

// CVEスコアの丸バッジ
export const CveCircle = ({ cve }: { cve: number | undefined }) => {
  const color = cveColor(cve)
  return (
    <span
      className={clsx(
        'text-size-12 inline-flex w-fit items-center justify-center rounded-md p-1 whitespace-nowrap',
        cve == 5 ? 'text-white' : 'text-primary'
      )}
      style={{ background: color }}
    >
      {cve ? cve + ':' + cveLabel(cve) : '-'}
    </span>
  )
}

// 検索順位の表示（GSC未連携時はプレースホルダ）
const SearchRankText = ({ rank }: { rank: number }) => {
  if (rank > 0) {
    return (
      <Text size={12} color={rank <= 10 ? TextColor.SUCCESS : TextColor.CAUTION}>
        {rank}位
      </Text>
    )
  }
  return <Text size={12}>GSC連携時に表示</Text>
}

const HEADERS: { key: SortKey | null; label: string }[] = [
  { key: 'keyword', label: 'キーワード' },
  { key: 'monthly_search_volume', label: '月間検索数' },
  { key: 'cve', label: 'CVE' },
  { key: 'search_rank', label: '検索順位' },
  { key: null, label: 'URL' }
]

interface KeywordRowProps {
  kw: KeywordItem
  index: number
  withRelationType: boolean
  selected: boolean
  onSelect: (kw: KeywordItem) => void
}

const KeywordRow = ({ kw, index, withRelationType, selected, onSelect }: KeywordRowProps) => {
  const background = selected ? 'info' : 'primary'

  return (
    <TableRow
      key={index}
      hoveredOn={!selected}
      attributes={{ onClick: () => onSelect(kw), style: { cursor: 'pointer' } }}
    >
      <TableRowItem background={background}>
        <ItemAlign>
          <Badge badgeType={kw.contains_input ? BadgeType.KEYWORD : BadgeType.KEYWORD_ON_BG_FILL}>
            {kw.keyword}
          </Badge>
          {withRelationType && kw.relation_type && <Badge size={10}>{kw.relation_type}</Badge>}
        </ItemAlign>
      </TableRowItem>
      <TableRowItem background={background}>
        <Text size={12}>{(kw.monthly_search_volume || 0).toLocaleString()}</Text>
      </TableRowItem>
      <TableRowItem background={background}>
        <CveCircle cve={kw.cve} />
      </TableRowItem>
      <TableRowItem background={background}>
        <SearchRankText rank={kw.search_rank} />
      </TableRowItem>
      <TableRowItem background={background} width={200}>
        {kw.ranking_url ? (
          <TextButton size={12} link={kw.ranking_url}>
            {kw.ranking_url}
          </TextButton>
        ) : (
          <Text size={12}>GSC連携時に表示</Text>
        )}
      </TableRowItem>
    </TableRow>
  )
}

// キーワード一覧テーブル（フィルタ・ソート・グループ表示。複数選択可）
const KeywordAnalysisTable = ({ keywords, inputKeyword, selectedKeywords, onSelect }: Props) => {
  const [filterMode, setFilterMode] = useState<FilterMode>('input')
  const [searchText, setSearchText] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('monthly_search_volume')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  // 選択済みなら解除、未選択なら追加する
  const toggleKeyword = (kw: KeywordItem) => {
    const exists = selectedKeywords.some((k) => k.keyword === kw.keyword)
    onSelect(
      exists ? selectedKeywords.filter((k) => k.keyword !== kw.keyword) : [...selectedKeywords, kw]
    )
  }

  const filteredKeywords = useMemo(() => {
    let kws = keywords
    if (filterMode === 'input') {
      kws = kws.filter((k) => k.contains_input)
    }
    if (searchText) {
      kws = kws.filter((k) => k.keyword.includes(searchText))
    }
    return [...kws].sort((a, b) => {
      const av = a[sortKey] ?? 0
      const bv = b[sortKey] ?? 0
      return sortOrder === 'desc' ? (bv > av ? 1 : -1) : av > bv ? 1 : -1
    })
  }, [keywords, filterMode, searchText, sortKey, sortOrder])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortOrder('desc')
    }
  }

  return (
    <div className='w-full'>
      <Line />
      <div className='flex flex-wrap items-center gap-2 p-3'>
        <Selector
          name='filterMode'
          size={SelectorSize.AUTO}
          choices={[
            { name: '「' + inputKeyword + '」を含む', value: 'input' },
            { name: '関連するすべて', value: 'all' }
          ]}
          value={filterMode}
          onChange={(e) => setFilterMode(e.target.value as FilterMode)}
        />
        <div className='flex-1' />
        <TextField
          type='search'
          size={TextBoxSize.LARGE}
          value={searchText}
          placeholder='キーワードで検索する'
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className='ml-auto'>
          <Text size={12} color={TextColor.SECONDARY}>
            全{keywords.length}件
          </Text>
        </div>
      </div>
      {/* テーブル */}
      <table className='w-full'>
        <thead>
          <TableRow>
            {HEADERS.map((h) => (
              <TableHeadItem key={h.label}>
                {h.key ? (
                  <ListHeaderSortButton
                    order={
                      sortKey === h.key
                        ? sortOrder == 'desc'
                          ? 'desc'
                          : sortOrder === 'asc'
                            ? 'asc'
                            : undefined
                        : undefined
                    }
                    onChange={(order) => handleSort(h.key as SortKey)}
                    text={h.label}
                  />
                ) : (
                  <Text size={12} color={TextColor.SECONDARY}>
                    {h.label}
                  </Text>
                )}
              </TableHeadItem>
            ))}
          </TableRow>
        </thead>
        <tbody>
          {filteredKeywords.map((kw, i) => (
            <KeywordRow
              key={i}
              kw={kw}
              index={i}
              withRelationType={true}
              selected={selectedKeywords.some((sk) => sk.keyword === kw.keyword)}
              onSelect={toggleKeyword}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default KeywordAnalysisTable
